import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AppButton from "../../components/form/AppButton";
import AppTextField from "../../components/form/AppTextField";
import { AuthPageShell } from "../../components/layout";
import sessionAuthApi from "../../services/sessionAuth";

export default function VerifyEmailUI() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const linkToken = (params.get("token") || "").trim();
  const autoStarted = useRef(false);

  const [email, setEmail] = useState("");
  const [verifying, setVerifying] = useState(!!linkToken);
  const [verified, setVerified] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!linkToken || autoStarted.current) {
      return;
    }
    autoStarted.current = true;
    setVerifying(true);
    setError(null);
    setMessage(null);
    sessionAuthApi
      .verifyEmail(linkToken)
      .then((r) => {
        setVerified(true);
        setMessage(r.message || t("auth.verifyEmail.success"));
      })
      .catch((e: any) => {
        setVerified(false);
        setError(e?.message || t("auth.verifyEmail.failed"));
      })
      .finally(() => setVerifying(false));
  }, [linkToken, t]);

  const onResend = async () => {
    setError(null);
    setMessage(null);
    setResending(true);
    try {
      const r = await sessionAuthApi.resendVerification(email.trim());
      setMessage(r.message || t("auth.verifyEmail.resendSent"));
    } catch (err: any) {
      setError(err?.message || t("auth.verifyEmail.resendFailed"));
    } finally {
      setResending(false);
    }
  };

  const showResend = !verifying && !verified;

  return (
    <AuthPageShell
      title={t("auth.verifyEmail.title")}
      subtitle={
        linkToken
          ? t("auth.verifyEmail.linkSubtitle")
          : t("auth.verifyEmail.manualSubtitle")
      }
      footer={
        !verifying && !showResend && !verified ? (
          <Link to="/login">{t("auth.verifyEmail.backToLogin")}</Link>
        ) : undefined
      }
    >
      <Stack spacing={2}>
        {verifying && (
          <Stack alignItems="center" spacing={1.5} py={3}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              {t("auth.verifyEmail.activating")}
            </Typography>
          </Stack>
        )}

        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}

        {verified && (
          <Box
            component={Link}
            to="/login"
            sx={{ display: "block", textDecoration: "none" }}
          >
            <AppButton variant="contained" fullWidth busyIgnore>
              {t("auth.verifyEmail.goToLogin")}
            </AppButton>
          </Box>
        )}

        {showResend && (
          <Stack spacing={2}>
            {!linkToken && (
              <Alert severity="info">{t("auth.verifyEmail.checkInbox")}</Alert>
            )}
            <Typography variant="subtitle2">{t("auth.verifyEmail.resendTitle")}</Typography>
            <AppTextField
              label={t("auth.verifyEmail.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <AppButton
              onClick={onResend}
              fullWidth
              variant="outlined"
              loading={resending}
              disabled={!email.trim()}
            >
              {t("auth.verifyEmail.resend")}
            </AppButton>
            <Box>
              <Link to="/login">{t("auth.verifyEmail.backToLogin")}</Link>
            </Box>
          </Stack>
        )}
      </Stack>
    </AuthPageShell>
  );
}
